import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { TableSortLabel } from "@mui/material";
import {
  getComparator,
  stableSort,
} from "../../../../common-utilities/sortingUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";
import CommonSearchCriteria from "../../common/CommonSearchCriteria";
import {
  GetWaterProfilesByCriteria,
  DeleteWaterProfile,
  SaveWaterProfile,
  AlterActiveState,
} from "../../../services/ClientWaterProfileService";
import UserService from "../../../../globel-constants/UserService";
import ViewWaterProfile from "./ViewWaterProfile";
import DuplicateWaterProfileDialog from "./DuplicateWaterProfileDialog";
import CommonLoader from "../../common/CommonLoader";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { waterProfileStatusList } from "../../common/statusLists";
import DoneAllIcon from "@mui/icons-material/DoneAll";

const WaterProfilesTable = () => {
  const columns = [
    { id: "id", label: "ID" },
    { id: "accountNumber", label: "Account" },
    { id: "type", label: "Type" },
    { id: "calcium", label: "Calcium" },
    { id: "totalAlkalinity", label: "Alkalinity" },
    { id: "magnesium", label: "Magnesium" },
    { id: "pH", label: "pH" },
    { id: "sodium", label: "Sodium" },
    { id: "chloride", label: "Chloride" },
    { id: "tds", label: "TDS" },
    { id: "actions", label: "Actions" },
  ];

  const rowsPerPageOptions = [10, 25, 50, 100, 200];
  const chemicalFields = [
    "calcium",
    "totalAlkalinity",
    "magnesium",
    "pH",
    "sodium",
    "chloride",
    "tds",
  ];

  const [profilesData, setProfilesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [duplicateAccountNumber, setDuplicateAccountNumber] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [searchCriteria, setSearchCriteria] = useState({
    searchText: "",
    orderStatus: "Active",
    startDate: "",
    endDate: "",
    pageNumber: 0,
    pageSize: 10,
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfiles(searchCriteria);
  }, [isDeleted]);

  useEffect(() => {
    if (!profilesData) return;
    const filteredResults = profilesData.filter((profile) =>
      Object.entries(profile).some(([key, value]) => {
        if (typeof value === "string") {
          return value.toLowerCase().includes(searchTerm.toLowerCase());
        }
        return false;
      })
    );
    setFilteredData(filteredResults);
  }, [searchTerm, profilesData]);

  const fetchProfiles = async (searchCriteria) => {
    try {
      setLoading(true);
      const response = await GetWaterProfilesByCriteria(
        searchCriteria.startDate || "",
        searchCriteria.endDate || "",
        searchCriteria.orderStatus,
        searchCriteria.searchText || ""
      );
      setProfilesData(response.results);
      setFilteredData(response.results);
    } catch (error) {
      console.error("Error fetching profiles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSearchCriteria = async (changedSearchCriteria) => {
    const updatedSearchCriteria = {
      ...searchCriteria,
      startDate: changedSearchCriteria.startDate || "",
      endDate: changedSearchCriteria.endDate || "",
      searchText: changedSearchCriteria.searchText || "",
      orderStatus: changedSearchCriteria.orderStatus,
      pageNumber: 0,
    };
    setSearchCriteria(updatedSearchCriteria);
    fetchProfiles(updatedSearchCriteria);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (profile) => setSelectedProfile(profile);
  const handleBackClick = () => {
    setSelectedProfile(null);
    setSearchTerm("");
  };

  const handleDeleteClick = (profile) => {
    setSelectedProfile(profile);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
    setSelectedProfile(null);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      setLoading(true);
      const response = await DeleteWaterProfile(selectedProfile.id);
      if (response.results === true) {
        NotificationManager.success("Profile deleted successfully", "Success");
        setIsDeleted(!isDeleted);
      } else {
        NotificationManager.error("Error deleting profile", "Error");
      }
    } catch (error) {
      NotificationManager.error("Error deleting profile", "Error");
    } finally {
      setLoading(false);
      setSelectedProfile(null);
      setIsDeleteConfirmationOpen(false);
    }
  };
  const handleToggleActive = async (profileId) => {
    try {
      setLoading(true);
      const response = await AlterActiveState(profileId);
      if (response.results === true) {
        NotificationManager.success(
          "Profile status updated successfully",
          "Success"
        );
        setIsDeleted(!isDeleted);
      } else {
        NotificationManager.error("Error updating profile status", "Error");
      }
    } catch (error) {
      NotificationManager.error("Error updating profile status", "Error");
    } finally {
      setLoading(false);
    }
  };
  const handleAddProfileClick = () => navigate("/water-profiles/add");
  const handleEditProfileClick = (profile) =>
    navigate(`/water-profiles/${profile.id}`);

  // const handleDuplicateClick = (profile) => {
  //   setDuplicateAccountNumber(profile.accountNumber);
  //   setSelectedProfile(profile);
  //   setIsDuplicateDialogOpen(true);
  // };
const handleDuplicateClick = (profile) => {
  setSelectedProfile(profile);
  setShowDuplicateDialog(true); // Open dialog
};

  const handleDuplicateConfirm = async (data) => {
    if (!data.type.trim()) {
      NotificationManager.error(
        "Please provide a valid water profile",
        "Error"
      );
      return;
    }
    try {
      setLoading(true);
      const profileData = {
        ...selectedProfile,
        accountNumber: data.accountNumber,
        type: data.type,
        id: undefined,
      };
      const response = await SaveWaterProfile(profileData);
      if (response.results) {
        NotificationManager.success(
          "Profile duplicated successfully",
          "Success"
        );
        setIsDeleted(!isDeleted);
      }
    } catch (error) {
      NotificationManager.error("Error duplicating profile", "Error");
    } finally {
      setLoading(false);
      handleDuplicateDialogClose();
    }
  };

  const handleDuplicateDialogClose = () => {
    setIsDuplicateDialogOpen(false);
    setDuplicateAccountNumber("");
    setSelectedProfile(null);
  };

  const renderActions = (profile) => (
    <div style={{ minWidth: "120px", display: "flex" }}>
      <Tooltip title={profile.isActive ? "Mark Inactive" : "Mark Active"}>
        <IconButton
          aria-label="toggle-active"
          style={{ color: "#c603fc" }}
          onClick={() => handleToggleActive(profile.id)}
        >
          <DoneAllIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit Profile">
        <IconButton
          aria-label="edit"
          style={{ color: "orange" }}
          onClick={() => handleEditProfileClick(profile)}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="View Profile">
        <IconButton
          aria-label="view"
          style={{ color: "green" }}
          onClick={() => handleViewClick(profile)}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete Profile">
        <IconButton
          aria-label="delete"
          style={{ color: "red" }}
          onClick={() => handleDeleteClick(profile)}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Duplicate Profile">
        <IconButton
          aria-label="copy"
          style={{ color: "#03d7fc" }}
          onClick={() => handleDuplicateClick(profile)}
        >
          <ContentCopyIcon />
        </IconButton>
      </Tooltip>
    </div>
  );

  return (
    <>
      <span className="title">Water Quality Profiles</span>
      <hr />
      {selectedProfile ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewWaterProfile profile={selectedProfile} />
        </>
      ) : (
        <>
          <div style={{ marginBottom: "20px" }}>
            <CommonSearchCriteria
              orderStatusOptions={waterProfileStatusList}
              onSearch={handleSearchCriteria}
            />
          </div>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search in current data"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "83%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddProfileClick}
            >
              New
            </Button>
          </div>

          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.id}>
                        {col.id === "actions" ? (
                          col.label
                        ) : (
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            onClick={() => handleRequestSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {stableSort(filteredData, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((profile, index) => (
                      <TableRow
                        key={profile.id}
                        sx={{
                          backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#fff",
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        {columns.slice(0, -1).map((col) => (
                          <TableCell key={col.id}>
                            {chemicalFields.includes(col.id)
                              ? profile[col.id]?.toFixed(2) || "N/A"
                              : profile[col.id] || "N/A"}
                          </TableCell>
                        ))}
                        <TableCell>{renderActions(profile)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      )}

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        message="Are you sure you want to delete this water profile?"
      />

     <DuplicateWaterProfileDialog
  open={showDuplicateDialog}
  onClose={() => setShowDuplicateDialog(false)}
  onConfirm={(data) => {
    console.log("Selected:", data);
    handleDuplicateConfirm(data)
    // You can now handle the duplication logic here using `data`
    setShowDuplicateDialog(false); // Close dialog after action
  }}
/>



      <CommonLoader loading={loading} />
    </>
  );
};

export default WaterProfilesTable;
